'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'

import { GlowFrame } from '@/components/atoms/glow-frame'
import { TILT_GROUP_ITEM, TiltGroup } from '@/components/atoms/tilt-group.client'
import { PageHeader } from '@/components/molecules/page-header'
import { RestockAlertForm } from '@/components/molecules/restock-alert.client'
import { TierCard } from '@/components/molecules/tier-card.client'
import { CONTAINER, PAGE_TOP, SCREEN_TOP } from '@/lib/layout'
import { formatPlnMinor } from '@/lib/money'
import type { ServiceView } from '@/lib/services'
import { RADIUS } from '@/lib/surface'
import { OFFER_SCREEN } from '@/lib/tier-fit'
import { ACCENT_TEXT } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Editing offer: the montage services × their tiers and their comparison rows.
 *
 * Rendered by ONE route again — `/pl/montaz`, long forms and reels together,
 * owner's call to merge them back. It was two for a while, when the reels had
 * `/pl/rolki` and their own nav entry. The route still hands this component
 * the slice of the catalogue it sells, so the page knows nothing about which
 * services exist; what it does NOT sell is written down in `lib/services.ts`.
 *
 * The tab switch survives for the case it was built for: more than one service
 * on one page. With a single service it is not drawn at all — a tablist holding
 * one tab is a control that cannot do anything, and it would sit above a
 * heading that already names the thing.
 *
 * The order configurator this page used to carry is GONE, and so is the one on
 * `/pl/grafika`: they were two implementations of the same arithmetic, neither
 * of which could handle a date or a city, so neither could ever be reused by
 * studio or produkcja. `/pl/kreator` is the one that can, and every tier card
 * here hands off to it with the package already chosen. This page sells; the
 * creator counts.
 *
 * ── WHAT THE PAGE IS MADE OF NOW ─────────────────────────────────────────
 * The tier card is `TierCard` — one file, shared with `/pl/miniaturki`, where
 * before this exact markup lived twice and could only ever be half changed.
 * There is no banner under the switch. One was built — a coloured band with the
 * category's glyph, its blurb and the ladder's price span — and the owner took
 * it out: it put a second heading between a headline and three cards that
 * already carry every figure on it. The category's own sentence stays, as a
 * line, which is what it was.
 */

/** What a level's card says when the switch is on the package, or `null` without one. */
function packFor(netMinor: number | undefined, listUnitNet: number, size: number) {
  if (netMinor === undefined) return null
  return {
    size,
    price: formatPlnMinor(netMinor),
    listPrice: formatPlnMinor(Math.round(listUnitNet * 100) * size),
    perUnit: formatPlnMinor(Math.round(netMinor / size)),
  }
}

export function MontagePageView({
  locale,
  services,
  packs = {},
  packSize = 10,
}: {
  locale: string
  services: ServiceView[]
  /**
   * Package prices by service and level, net grosze — only the reels have one.
   * Owner, 2026-10-15: „pakiety 10 sztuk z opcją przełącznika na stronie".
   */
  packs?: Record<string, Record<string, number>>
  packSize?: number
}) {
  // Which page this is, recorded with the alert so support can see where
  // somebody was standing. One address sells montage today; the component is
  // still handed a slice rather than asked to know that.
  const pathname = usePathname()
  const [activeSlug, setActiveSlug] = useState(services[0]?.slug ?? '')
  const [packOn, setPackOn] = useState(false)

  const service = services.find((s) => s.slug === activeSlug) ?? services[0]
  const servicePacks = service ? packs[service.slug] : undefined

  // An empty catalogue is a database that failed to load or a page nobody has
  // filled in yet. Either way the pitch and the contact route still stand.
  if (!service) {
    return (
      <div className="relative w-full overflow-x-hidden">
        <div className={cn(CONTAINER, PAGE_TOP, 'relative z-10')}>
          <PageHeader>
            Oddaj nam montaż. <span className={ACCENT_TEXT}>Wróć do nagrywania.</span>
          </PageHeader>
          <Link
            href={`/${locale}/kontakt`}
            className="mt-8 inline-flex rounded-full border border-white/15 px-6 py-3 text-[0.95rem] font-semibold text-white/80 transition hover:border-white/40 hover:text-white"
          >
            Napisz do nas
          </Link>
        </div>
      </div>
    )
  }

  const plans = service.plans
  // See the twin on `/pl/grafika`: `null` is "asked, nothing free", `undefined`
  // is "nobody asked", and only the first is something to wait for.
  const soldOut = plans.filter((plan) => plan.readyFrom === null).map((plan) => plan.slug)

  return (
    <TiltGroup className="relative w-full overflow-x-hidden">
      {/* ONE SCREEN: head, switch and the three levels, with nothing of the
          ladder under the fold. `lib/tier-fit.ts` holds the geometry and the
          reasoning; `/pl/miniaturki` stands in the same one. */}
      <div className={cn(CONTAINER, SCREEN_TOP, OFFER_SCREEN, 'relative z-10')}>
        {/* No headline. Owner, 16.10: the page opens on the switch and the three
            levels — *„ograniczamy tekst do minimum, chyba że chodzi o ofertę"*.
            The heading stays for screen readers and the document outline. */}
        <h1 className="sr-only">Montaż — cennik</h1>

        {/* Switch, levels and the restock form as ONE block, centred in the
            screen — so the switch sits right above the cards and the form right
            under them, instead of the switch pinned to the header and the form
            to the foot of the screen. */}
        <div className="flex flex-col md:flex-1 md:justify-center">
          {/* ── CATEGORY SWITCH ──────────────────────────────────
            Only when there is something to switch BETWEEN. The page ships
            Rolki, Podcast, Lifestyle and Inne — reels among them since the
            merge, Newsy and Vlogi gone since the owner withdrew them — and
            this is how somebody moves between them. A catalogue narrowed to
            one surviving category would draw no switch at all, which is the
            case this guard is for.

            CENTRED, in a wrapper rather than on the strip itself: the pill takes
            its width from its tabs, so it has to be an inline box inside a
            centring one. `flex-wrap` on the strip is what keeps five tabs off a
            phone's horizontal scrollbar — the page clips overflow, so without it
            the last tab would be cut rather than moved. It mattered at five
            tabs and stays at four: the count is the catalogue's to change.

            PLAIN PILLS, white when chosen. The strip briefly carried the brand
            gradient on the active tab and a coloured glyph on every one of
            them; the owner put it back. It is a switch above the thing it
            switches, and a row of tinted icons made it compete with the cards
            underneath — which are where the colour now lives on its own. */}
          {services.length > 1 ? (
            <div className="mt-[clamp(1rem,3svh,2.5rem)] flex justify-center">
              <div
                role="tablist"
                aria-label="Kategoria montażu"
                className={cn(
                  RADIUS.strip,
                  'inline-flex flex-wrap justify-center gap-1.5 border border-white/[0.12] bg-white/[0.04] p-1.5 backdrop-blur-md',
                )}
              >
                {services.map((s) => {
                  const isOn = s.slug === service.slug
                  return (
                    <button
                      key={s.slug}
                      type="button"
                      role="tab"
                      aria-selected={isOn}
                      onClick={() => setActiveSlug(s.slug)}
                      className={cn(
                        'px-5 py-2.5 text-[0.95rem] font-semibold transition',
                        RADIUS.stripTab,
                        isOn ? 'bg-white text-neutral-900' : 'text-white/65 hover:text-white',
                      )}
                    >
                      {s.name}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}

          {/* ── ONE REEL OR TEN ──────────────────────────────────
            Only where the service HAS a package — the reels today. The same
            pill as the category switch, so it reads as a second switch rather
            than as a new kind of control. */}
          {servicePacks ? (
            <div className="mt-[clamp(0.5rem,1.6svh,1rem)] flex justify-center">
              <div
                role="radiogroup"
                aria-label="Ile rolek"
                className={cn(
                  RADIUS.strip,
                  'inline-flex gap-1.5 border border-white/[0.12] bg-white/[0.04] p-1.5 backdrop-blur-md',
                )}
              >
                {[
                  { on: false, label: '1 rolka' },
                  { on: true, label: `Pakiet ${packSize} rolek` },
                ].map((option) => {
                  const isOn = packOn === option.on
                  return (
                    <button
                      key={option.label}
                      type="button"
                      role="radio"
                      aria-checked={isOn}
                      onClick={() => setPackOn(option.on)}
                      className={cn(
                        'px-5 py-2 text-[0.9rem] font-semibold transition',
                        RADIUS.stripTab,
                        isOn ? 'bg-white text-neutral-900' : 'text-white/65 hover:text-white',
                      )}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}

          {/* ── TIER CARDS ─────────────────────────────────────────
            Flat, and the ROW leans with the pointer (`TiltGroup`) — the three
            levels move as one object. Each card wears `GlowFrame`: a light that
            travels round the frame, grey on the first rung and in the brand
            hues above it, passed from card to card by `phase`. The cards' own
            lean is off (`tilt={false}`) or a card would slide inside its frame.

            The fan is gone from this page, owner's call on the 16.10 design.
            `/pl/miniaturki` dropped it the same day, so `lib/tier-fan.ts` has no
            caller left and is deleted; git history has it.

            The price runs larger than the shared card's default here — the
            override on `--tier-price` reaches the card's own variable because
            `[&_article]` outranks the card's single-class one. */}
          <ul
            className={cn(
              'mt-[clamp(0.75rem,2.4svh,1.5rem)] grid grid-cols-1 gap-6 md:grid-cols-3',
              '[&_article]:[--tier-price:min(28cqw,clamp(2.8rem,11svh_-_1.6rem,7.2rem))]',
              TILT_GROUP_ITEM,
            )}
          >
            {plans.map((plan, index) => (
              <li key={plan.slug} className={cn('h-full', plan.popular && 'relative z-10')}>
                <GlowFrame neutral={index === 0} phase={index * 2.33}>
                  <TierCard
                    locale={locale}
                    service={service}
                    plan={plan}
                    level={index + 1}
                    levels={plans.length}
                    pack={
                      packOn ? packFor(servicePacks?.[plan.slug], plan.priceNet, packSize) : null
                    }
                    tilt={false}
                  />
                </GlowFrame>
              </li>
            ))}
          </ul>

          {/* Once, under the grid, however many levels are full — it used to be
            printed inside every sold-out card. It is a control now: leave an
            address and the level writes to you when it comes back. */}
          {soldOut.length > 0 ? (
            <RestockAlertForm
              offeringSlug={service.slug}
              planSlugs={soldOut}
              sourcePath={pathname}
            />
          ) : null}
        </div>

        {/* No comparison table here on purpose: the tier cards now carry every
            row themselves, grouped into what a level includes and what the one
            above adds, so a table below would only repeat them. */}
      </div>
    </TiltGroup>
  )
}
